import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listUserExtensionsDataTable,
  getUserExtensionFilterOptions,
} from '@kirin/api-client/api/extensions';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import type {
  PersonalExtensionFilterableField,
  PersonalExtensionFilterOptionField,
  PersonalExtensionSortField,
} from '@kirin/api-client/contracts/account-extensions';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Fetch personal extensions with DataTable pagination, filters, and sorting.
 */
export async function fetchUserExtensions(
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<ExtensionListItem>> {
  return listUserExtensionsDataTable(
    apiClient,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as PersonalExtensionSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );
}

/**
 * Fetch distinct filter option values for a personal extension column.
 * Returns paginated result shape to support infinite-scroll in filter dropdowns.
 */
export async function fetchExtensionFilterOptions(
  field: PersonalExtensionFilterableField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getUserExtensionFilterOptions(apiClient, {
    field: field as PersonalExtensionFilterOptionField,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Build the React Query key for a personal extension filter options request.
 */
export function getExtensionFilterOptionsQueryKey(
  field: PersonalExtensionFilterableField,
  params: { search?: string; limit?: number }
): readonly unknown[] {
  return queryKeys.extensions.filterOptions(field, params);
}
